import { takeLatest, call, put, select } from "redux-saga/effects";
import { createAction } from "redux-actions";
import { settingsApi, usersApi } from "../api";
import { getBaseUrlParams } from "../../../../utils/getBaseUrlParams";
import { stringify } from "../../../../utils/stringify";
import { showToast } from "../../../../utils/showToast";

export const SET_LOADING = "saga/users/SET_LOADING";
export const SET_UPDATE_LOADING = "saga/users/SET_UPDATE_LOADING";
export const SET_ACTIONS_LOADING = "saga/users/SET_ACTIONS_LOADING";

export const ADD_USER = "saga/users/ADD_USER";
export const ADD_USER_SUCCESS = "saga/users/ADD_USER_SUCCESS";

export const DUPLICATE_USER = "saga/users/DUPLICATE_USER";
export const DUPLICATE_USER_SUCCESS = "saga/users/DUPLICATE_USER_SUCCESS";

export const EDIT_USER = "saga/users/EDIT_USER";
export const EDIT_USER_SUCCESS = "saga/users/EDIT_USER_SUCCESS";

export const DELETE_USER = "saga/users/DELETE_USER";
export const DELETE_USER_SUCCESS = "saga/users/DELETE_USER_SUCCESS";

export const MIRROR_USER = "saga/users/MIRROR_USER";
export const MIRROR_USER_SUCCESS = "saga/users/MIRROR_USER_SUCCESS";

export const FETCH_SETTINGS = "saga/users/FETCH_SETTINGS";
export const FETCH_SETTINGS_SUCCESS = "saga/users/FETCH_SETTINGS_SUCCESS";

export const FETCH_USERS = "saga/users/FETCH_USERS";
export const FETCH_USERS_SUCCESS = "saga/users/FETCH_USERS_SUCCESS";

export const FETCH_ACTIONS = "saga/users/FETCH_ACTIONS";
export const FETCH_ACTIONS_SUCCESS = "saga/users/FETCH_ACTIONS_SUCCESS";

export const FETCH_USER_INFO = "saga/users/FETCH_USER_INFO";
export const FETCH_USER_INFO_SUCCESS = "saga/users/FETCH_USER_INFO_SUCCESS";

export const RESET_STATUS = "saga/users/RESET_STATUS";
export const SET_ERROR = "saga/users/SET_ERROR";
export const SET_STATUS = "saga/users/SET_STATUS";

export const TOGGLE_CORPORATE = "saga/users/TOGGLE_CORPORATE";
export const TOGGLE_CORPORATE_SUCCESS = "saga/users/TOGGLE_CORPORATE_SUCCESS";

export const CREATE_CUSTOM_PERMISSION_GROUP = "saga/users/CREATE_CUSTOM_PERMISSION_GROUP";
export const ADD_NEW_ROLE = "saga/users/ADD_NEW_ROLE";

const actionCreators = {
  fetchSettings: createAction(FETCH_SETTINGS),
  fetchUsers: createAction(FETCH_USERS),
  fetchActions: createAction(FETCH_ACTIONS),
  fetchUserInfo: createAction(FETCH_USER_INFO),
  toggleCorporate: createAction(TOGGLE_CORPORATE),
  createUser: createAction(ADD_USER),
  duplicateUser: createAction(DUPLICATE_USER),
  changeUser: createAction(EDIT_USER),
  mirrorUser: createAction(MIRROR_USER),
  deleteUser: createAction(DELETE_USER),
  resetStatus: createAction(RESET_STATUS),
  createPermissionGroup: createAction(CREATE_CUSTOM_PERMISSION_GROUP)
};

export const {
  fetchSettings,
  fetchUsers,
  fetchActions,
  fetchUserInfo,
  toggleCorporate,
  createUser,
  duplicateUser,
  changeUser,
  mirrorUser,
  deleteUser,
  resetStatus,
  createPermissionGroup
} = actionCreators;

export default function* watchUsers() {
  yield takeLatestSafe(FETCH_SETTINGS, getSettings);
  yield takeLatestSafe(FETCH_USERS, getUsers);
  yield takeLatestSafe(FETCH_ACTIONS, getActions);
  yield takeLatestSafe(TOGGLE_CORPORATE, switchCorporateToggle);
  yield takeLatestSafe(ADD_USER, addUser);
  yield takeLatestSafe(DUPLICATE_USER, copyUser);
  yield takeLatestSafe(MIRROR_USER, mirrorUserPermissions);
  yield takeLatestSafe(DELETE_USER, removeUser);
  yield takeLatestSafe(EDIT_USER, editUser);
  yield takeLatestSafe(FETCH_USER_INFO, getUserInfo);
  yield takeLatestSafe(CREATE_CUSTOM_PERMISSION_GROUP, createRole);
}

function takeLatestSafe(action, effect) {
  return takeLatest(action, tryCatch(effect));
}

function tryCatch(effect) {
  return function* (args) {
    try {
      yield call(effect, args);
    } catch (err) {
      console.error(err);
      if (err.response.status === 422) {
        yield put({ type: SET_ERROR, payload: err.response.data });
      }
    }
  };
}

function* getSettings() {
  yield put({ type: SET_LOADING, payload: true });

  const { organization_id } = getBaseUrlParams();

  const { data } = yield call(settingsApi.getSettings, { params: { organization_id } });

  yield put({
    type: FETCH_SETTINGS_SUCCESS,
    payload: data
  });

  yield put({ type: SET_LOADING, payload: false });
}

function* getUsers({ payload: { key, data } }) {
  yield put({ type: SET_UPDATE_LOADING, payload: true });

  const { organization_id } = getBaseUrlParams();
  const searchParam = {
    [key]: data.id ? data.id : typeof data == "string" ? data : data.map((d) => d.id)
  };
  const res = yield call(usersApi.getUsers, {
    organization_id,
    ...searchParam
  });

  yield put({
    type: FETCH_USERS_SUCCESS,
    payload: { users: res.data, searchParam }
  });

  yield put({ type: SET_UPDATE_LOADING, payload: false });
}

function* getActions({ payload }) {
  yield put({ type: SET_ACTIONS_LOADING, payload: true });

  const { organization_id } = getBaseUrlParams();

  const { data } = yield call(settingsApi.getActions, { params: { organization_id, id: payload } });

  yield put({
    type: FETCH_ACTIONS_SUCCESS,
    payload: data
  });

  yield put({ type: SET_ACTIONS_LOADING, payload: false });
}

function* getUserInfo({ payload }) {
  const { organization_id } = getBaseUrlParams();

  const { data } = yield call(usersApi.getUserInfo, { params: { organization_id, id: payload } });

  yield put({
    type: FETCH_USER_INFO_SUCCESS,
    payload: data
  });
}

function* switchCorporateToggle({ payload }) {
  const { organization_id } = getBaseUrlParams();

  yield call(usersApi.setUserAsCorporate, {
    organization_id,
    id: payload.id.toString()
  });

  yield put({
    type: TOGGLE_CORPORATE_SUCCESS,
    payload
  });
}

function* addUser({ payload }) {
  yield put({ type: SET_STATUS, payload: "pending" });

  const { organization_id } = yield getBaseUrlParams();

  yield call(usersApi.addUser, {
    organization_id,
    user: {
      ...payload,
      role_id: payload.role_id.id.toString(),
      location_ids: payload.location_ids.map((loc) => loc.id)
    }
  });

  // TODO: rework adding user to store instead of repeating search request after adding new user

  const currentSearchParam = yield select((state) => state.users.currentSearchParam);
  if (currentSearchParam) {
    const res = yield call(usersApi.getUsers, {
      params: {
        organization_id,
        ...currentSearchParam
      }
    });

    yield put({
      type: FETCH_USERS_SUCCESS,
      payload: { users: res.data }
    });
  }

  yield put({ type: SET_STATUS, payload: "success" });
}

function* copyUser({ payload }) {
  yield put({ type: SET_STATUS, payload: "pending" });

  const { organization_id } = getBaseUrlParams();

  let res = yield call(usersApi.duplicateUser, {
    organization_id,
    source_user_id: payload.id.toString(),
    user: payload.data
  });

  yield put({
    type: DUPLICATE_USER_SUCCESS,
    payload: res.data
  });

  yield put({ type: SET_STATUS, payload: "success" });
}

function* mirrorUserPermissions({ payload }) {
  const { organization_id } = getBaseUrlParams();

  try {
    let res = yield call(usersApi.mirrorUser, {
      organization_id,
      source_user_id: payload.id.toString(),
      user: { id: payload.data.map((user) => user.id) }
    });
    yield put({ type: MIRROR_USER_SUCCESS, payload: { data: res.data.users } });
    if (res.status == 200) {
      showToast(res.data.message);
    }
  } catch (err) {
    console.log(err);
    showToast(err?.response.data.error);
  }
}

function* createRole({ payload }) {
  yield put({ type: SET_STATUS, payload: "pending" });

  const { organization_id } = getBaseUrlParams();

  let res = yield call(usersApi.addUserPermissionsAsCustomRole, {
    organization_id,
    id: payload.id.toString(),
    role: { name: payload.name }
  });

  yield put({ type: ADD_NEW_ROLE, payload: res.data });
  yield put({ type: SET_STATUS, payload: "success" });
}

function* editUser({ payload: { id, data } }) {
  yield put({ type: SET_STATUS, payload: "pending" });

  const { organization_id } = getBaseUrlParams();

  let res = yield call(usersApi.updateUser, id, {
    organization_id,
    user: data
  });

  yield put({
    type: EDIT_USER_SUCCESS,
    payload: { id, data: res.data, haveNamesChanged: !!(data.first_name || data.last_name) }
  });
}

function* removeUser(data) {
  const { organization_id } = getBaseUrlParams();

  try {
    let res = yield call(usersApi.removeUser, { params: { organization_id, id: data.payload.id } });

    yield put({
      type: DELETE_USER_SUCCESS,
      payload: { id: data.payload.id }
    });

    if (res.status == 200) {
      showToast(res.data.message);
    }
  } catch (err) {
    console.log(err);
    showToast(err.response.data.error);
  }
}
